/**
 * Модель карточки «Загрузка 1С» (20.10.2026): ответы GET /api/eo/archive-1c/state и /pair/state → строки для экрана,
 * поле кода сопряжения (алфавит — @ub/shared/a1cPair) и тексты отказов. Без React — проверяется node:test (a1cPanel.test.ts).
 */
import { cleanPairInput, formatPairCode, normalizePairCode, PAIR_CODE_LEN } from "@ub/shared/a1cPair";

export interface A1cState {
  switch: "open" | "closed";
  key: string;
  sync_version: { code: string; applied: boolean; latest: string | null };
  journal: { table: boolean; indexes_ok: boolean; loads: { load_id: string; batches: number; targets: number; lineage: number }[] };
}
export interface PairState {
  now: string;
  /** живые запросы кода: одобренные — первыми, не больше 20 (сервер) */
  pending: { id: string; status: string; net: string; created_at: string; expires_at: string }[];
  /** 20.10: всего живых запросов (pending — первые из них) */
  pending_total?: number;
  /** все живые доступы загрузчика — отдельным запросом: чужие коды их не вытесняют */
  active: { id: string; by: string | null; by_name: string | null; net: string; issued_at: string; expires_at: string }[];
}

/** POST /pair/check: что за запрос стоит за кодом (20.10, до «Разрешить»). */
export interface PairCheck { net: string; age_s: number; same_net: boolean }

/** Текст окна перед «Разрешить»: когда и откуда запрошен код, та же ли сеть, что у телефона, и напоминание про чужой код. */
export function checkLines(r: PairCheck): string[] {
  const age = r.age_s < 60 ? "меньше минуты назад" : `${Math.round(r.age_s / 60)} мин назад`;
  return [
    `Код запрошен ${age}.`,
    ...(r.net ? [`Откуда: ${r.net}`] : []),
    r.same_net ? "Это та же сеть, что у вашего телефона."
      : "Сеть не та, что у телефона: так бывает, если компьютер в другом интернете или с VPN. Если загрузчик запускали не вы — не разрешайте.",
    "Вводите только код с экрана своего загрузчика.",
  ];
}

/** Обрыв связи (не ответ сервера): русский текст вместо «Failed to fetch» / «Load failed». */
export const offlineText = (fallback: string): string => `${fallback}: нет связи — проверьте интернет и повторите`;

/** Сколько живых запросов кода не показано (сервер отдаёт первые 20). */
export function hiddenPending(pair: PairState | null): number {
  return pair ? Math.max(0, (pair.pending_total ?? pair.pending.length) - pair.pending.length) : 0;
}

/** Поле кода: что показать в инпуте и готов ли код к «Разрешить». */
export function codeInput(raw: string): { view: string; code: string | null } {
  const cleaned = cleanPairInput(raw).slice(0, PAIR_CODE_LEN);
  return { view: formatPairCode(cleaned), code: normalizePairCode(cleaned) };
}

/** «20.10 23:15» по Оренбургу (UTC+5). */
export function orenTime(iso: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return "—";
  const d = new Date(ms + 5 * 3_600_000).toISOString();
  return `${d.slice(8, 10)}.${d.slice(5, 7)} ${d.slice(11, 16)}`;
}

const ERR: Record<string, string> = {
  code_format: "Код — 6 знаков, как на экране загрузчика (например K7M-42X).",
  code_unknown: "Код не найден или истёк. Запустите сопряжение на компьютере заново и введите новый код.",
  code_taken: "Этот код уже разрешён — загрузчик получает доступ. Повторно вводить не нужно.",
  too_many_attempts: "Слишком много неверных кодов. Подождите 10 минут.",
  eo_only: "Разрешать загрузку может только EO.",
  dev_auth_refused: "Нужен обычный вход EO (не dev).",
  loader_refused: "Этим входом нельзя управлять доступом загрузчика.",
  switch_unreadable: "Не удалось прочитать выключатель загрузки — повторите позже.",
};
/** Текст отказа по коду ответа роута (тело { ok:false, code }). */
export function errText(body: unknown, fallback: string): string {
  const code = body && typeof body === "object" && "code" in body ? String((body as { code: unknown }).code) : "";
  return ERR[code] ?? (code ? `${fallback} (${code})` : fallback);
}

export interface Line { label: string; value: string; tone: "ok" | "warn" | "muted" }
/** Сводка состояния: выключатель, ключ, версия схемы, доступы загрузчика. */
export function summary(st: A1cState | null, pair: PairState | null): Line[] {
  const out: Line[] = [];
  if (st) {
    out.push({ label: "Выключатель", value: st.switch === "open" ? "открыт" : "закрыт", tone: st.switch === "open" ? "warn" : "muted" });
    out.push({ label: "Ключ шифрования", value: st.key === "ok" ? "в порядке" : st.key, tone: st.key === "ok" ? "ok" : "warn" });
    out.push({ label: "Схема базы", value: st.sync_version.applied ? st.sync_version.code : `не применена (${st.sync_version.code})`, tone: st.sync_version.applied ? "ok" : "warn" });
  }
  if (pair) {
    const last = pair.active.reduce<string | null>((m, a) => (!m || a.expires_at > m ? a.expires_at : m), null);
    out.push({ label: "Доступ загрузчика", value: pair.active.length ? `активен: ${pair.active.length}, до ${orenTime(last!)}` : "нет", tone: pair.active.length ? "warn" : "muted" });
    const waiting = pair.pending_total ?? pair.pending.length;
    if (waiting) out.push({ label: "Ждут кода", value: String(waiting), tone: "muted" });
  }
  return out;
}

/** Строки загрузок журнала (порядок 2.2 — как отдаёт сервер). */
export function loadLines(st: A1cState | null): string[] {
  if (!st?.journal.table) return [];
  return st.journal.loads.map((l) => `${l.load_id} · пачек ${l.batches} · целей ${l.targets}`);
}
